/**
 * The API host this app talks to, and the switch to another one when it stops
 * answering.
 *
 * Every API call goes through `apiBase()` / `apiRequest()` instead of reading
 * `env.VITE_API_BASE_URL`. The candidates are, in order: the signed list svc-setting
 * serves (once one has been verified), then the build-time bootstrap list
 * (`VITE_API_BASE_URL` + `VITE_API_FALLBACK_BASE_URLS`). The first that answers is
 * pinned in localStorage, so a reload starts on the host that worked.
 *
 * Reachability and the list are two separate questions. A candidate is reachable
 * when its gateway answers `<base>/ping`, which needs nothing behind the gateway;
 * the signed list is then fetched from it on a best-effort basis. Gating the switch
 * on the list endpoint made every host look blocked whenever svc-setting, its
 * database or its signing key was missing.
 *
 * A list from the network is accepted only when it verifies under
 * `VITE_EDGE_HOSTS_PUBLIC_KEY`, was signed for this deployment
 * (`VITE_EDGE_HOSTS_ENVIRONMENT`), was issued within `EDGE_HOSTS_MAX_AGE_MS`, and is
 * not older than the one held. A v2 list already stored in this browser is only
 * re-verified (signature and deployment) at startup, never aged out: it was accepted
 * once, and dropping it would throw away the hosts an operator added for exactly the
 * player who has been away longest. A stored legacy v1 list is different: it names no
 * deployment, so nothing but its age bounds where it came from, and it gets the
 * same max age a list from the network does. Without a key no list is ever accepted
 * and only the bootstrap list is used.
 *
 * Storage keys are NOT tenant-namespaced: the tenant id comes from /site-config,
 * which needs a working host first.
 */
import { EDGE_HOSTS_MAX_AGE_MS, verifyEdgeHosts, type EdgeHostsPayload } from '@b1fr0st/shared/edge-hosts';
import { env } from '../../env.js';

const PIN_KEY = 'b1fr0st.player.apiHost';
const LIST_KEY = 'b1fr0st.player.apiHostList';
const MOVED_KEY = 'b1fr0st.player.apiHostMoved';
export const hostPoolTimeouts = {
  /** One probe of one candidate: ping, or the list download. */
  probeMs: 4000,
  /**
   * How long a READ (GET/HEAD/OPTIONS, no body) may wait for response headers
   * before its host counts as gone. Above the gateway's own upstream timeout
   * (PROXY_TIMEOUT_MS, 15s), so a slow service answers 504 through a healthy
   * gateway long before this fires; what it catches is a host that swallows
   * packets and never answers at all. A write gets no such deadline: fetch cannot
   * tell "the body is still uploading" from "the host is silent", and a 12 MB
   * deposit slip on a slow uplink takes minutes to send (see `apiRequest`).
   */
  requestHeadersMs: 20_000,
};
export const HOST_LIST_REFRESH_MS = 10 * 60_000;
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

let signed: EdgeHostsPayload | null = null;
let failoverInflight: Promise<string | null> | null = null;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the pool still works for this page load.
  }
}

/**
 * An abort signal that fires after `ms`, or when `outer` does. `clear()` stops the
 * timer only; an `outer` abort still reaches a body that is being read.
 */
function deadline(ms: number, outer?: AbortSignal | null): { signal: AbortSignal; clear: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('API host did not answer in time', 'TimeoutError')), ms);
  if (outer) {
    if (outer.aborted) controller.abort(outer.reason);
    else outer.addEventListener('abort', () => controller.abort(outer.reason), { once: true });
  }
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

async function fetchWithin(ms: number, url: string, init?: RequestInit): Promise<Response> {
  const { signal, clear } = deadline(ms, init?.signal);
  try {
    return await fetch(url, { ...init, signal });
  } finally {
    clear();
  }
}

function bootstrapHosts(): string[] {
  const raw = [env.VITE_API_BASE_URL, ...(env.VITE_API_FALLBACK_BASE_URLS ?? '').split(',')];
  return raw.map((h) => h.trim().replace(/\/+$/, '')).filter((h) => h.length > 0);
}

/** Candidate bases in failover order, without duplicates. */
export function apiHosts(): string[] {
  return [...new Set([...(signed?.hosts ?? []), ...bootstrapHosts()])];
}

/** The base URL (e.g. `https://api-prod.b1fr0st.com/api/v1`) every request should use now. */
export function apiBase(): string {
  const hosts = apiHosts();
  const pinned = read(PIN_KEY);
  return pinned !== null && hosts.includes(pinned) ? pinned : hosts[0]!;
}

type ListSource = 'network' | 'storage';

async function acceptList(doc: unknown, source: ListSource): Promise<boolean> {
  const key = env.VITE_EDGE_HOSTS_PUBLIC_KEY;
  if (!key) return false;
  const payload = await verifyEdgeHosts(key, doc);
  if (payload === null) return false;
  if (payload.v === 1) {
    // Carries no deployment and no signing time; only a copy this browser already
    // held from before v2 is kept, and a fresh v2 copy replaces it.
    if (source === 'network') return false;
  } else if (payload.environment !== env.VITE_EDGE_HOSTS_ENVIRONMENT) {
    return false;
  }
  const issuedAt = Date.parse(payload.issuedAt);
  const aged = source === 'network' || payload.v === 1;
  if (aged && issuedAt < Date.now() - EDGE_HOSTS_MAX_AGE_MS) return false;
  if (signed !== null && issuedAt < Date.parse(signed.issuedAt)) return false;
  // A new list reorders candidates but must not move a working app: the refresh
  // cookie belongs to the host in use, so a move is a logout. Pin it first.
  write(PIN_KEY, apiBase());
  signed = payload;
  write(LIST_KEY, JSON.stringify(doc));
  return true;
}

/** Re-verifies the stored list. Call once at startup; a list that no longer verifies is dropped. */
export async function initHostPool(): Promise<void> {
  const raw = read(LIST_KEY);
  if (raw === null) return;
  try {
    if (!(await acceptList(JSON.parse(raw), 'storage'))) write(LIST_KEY, null);
  } catch {
    write(LIST_KEY, null);
  }
}

/** Pulls the latest list from the current host. Never throws. */
export async function refreshHostList(): Promise<void> {
  const { signal, clear } = deadline(hostPoolTimeouts.probeMs);
  try {
    const res = await fetch(`${apiBase()}/edge-hosts`, { signal, credentials: 'omit' });
    if (res.ok) await acceptList(await res.json(), 'network');
  } catch {
    // Unreachable, slow or malformed: keep the list we have.
  } finally {
    clear();
  }
}

/**
 * True when the gateway at `base` answers its ping. Any answer below 500 counts: a
 * block page cannot impersonate the host through TLS. A 5xx is an edge in front of
 * a gateway that is not answering. A gateway that predates the ping route answers
 * 404, which counts — but the browser lets this page read it only if that answer
 * carries CORS headers for this page's origin, so from an origin such a gateway
 * does not allow the read fails and it looks unreachable. Current gateways answer
 * the ping with `access-control-allow-origin: *`, whatever the origin.
 */
async function isReachable(base: string): Promise<boolean> {
  try {
    const res = await fetchWithin(hostPoolTimeouts.probeMs, `${base}/ping`, { cache: 'no-store', credentials: 'omit' });
    return res.status < 500;
  } catch {
    return false;
  }
}

async function runFailover(failedBase: string): Promise<string | null> {
  // One request failing is not the host failing. Moving is a logout (the refresh
  // cookie belongs to the host in use), so the current host is asked first, and
  // staying is the answer whenever it still answers.
  if (await isReachable(failedBase)) return failedBase;
  for (const candidate of apiHosts()) {
    if (candidate === failedBase) continue;
    if (!(await isReachable(candidate))) continue;
    write(PIN_KEY, candidate);
    write(MOVED_KEY, '1');
    void refreshHostList();
    return candidate;
  }
  return null;
}

/**
 * After a request to `failedBase` failed: `failedBase` itself when it still answers
 * its ping, otherwise the first other host that does, pinned. Null when none
 * answers. Concurrent callers share one probe.
 */
export function failoverFrom(failedBase: string): Promise<string | null> {
  if (apiBase() !== failedBase) return Promise.resolve(apiBase());
  failoverInflight ??= runFailover(failedBase).finally(() => {
    failoverInflight = null;
  });
  return failoverInflight;
}

/** Anything that sends a body or is not a read: never re-sent, never given a header deadline. */
function isWrite(init?: RequestInit): boolean {
  return !SAFE_METHODS.has((init?.method ?? 'GET').toUpperCase()) || init?.body != null;
}

/**
 * `fetch(apiBase() + path, init)` with failover when the host does not answer.
 *
 * A read that errors at the network layer, or gets no response headers within
 * `hostPoolTimeouts.requestHeadersMs`, is re-sent once on another host — if the
 * current one no longer answers its ping; when it does, the error is the
 * request's, not the host's, and the app stays where it is.
 *
 * A write (any non-read method, or any request with a body) is never re-sent: it
 * may have reached the server, and sending it again could, say, create a second
 * deposit. It also gets no header deadline. fetch starts that clock before the
 * body is sent, so a deadline would cut off a deposit slip or a KYC photo (up to
 * 12 MB through the gateway) still uploading on a slow line, and a write is not
 * re-sent anyway, so the deadline would only decide a host switch that logs the
 * player out. A write that errors at the network layer asks the same question as
 * a read: stay if the host answers its ping, move otherwise, and rethrow either
 * way so the user's retry lands on a host that works.
 */
export async function apiRequest(path: string, init?: RequestInit): Promise<Response> {
  const base = apiBase();
  const write = isWrite(init);
  try {
    return write
      ? await fetch(`${base}${path}`, init)
      : await fetchWithin(hostPoolTimeouts.requestHeadersMs, `${base}${path}`, init);
  } catch (err) {
    if (init?.signal?.aborted) throw err;
    const next = await failoverFrom(base);
    if (next === null || next === base || write) throw err;
    return fetchWithin(hostPoolTimeouts.requestHeadersMs, `${next}${path}`, init);
  }
}

/**
 * True once, after the app switched hosts, then cleared. The refresh cookie
 * belongs to the old API host, so a session that dies after a switch died
 * because of the switch, and the login page should say so.
 */
export function consumeHostMoved(): boolean {
  const moved = read(MOVED_KEY) !== null;
  write(MOVED_KEY, null);
  return moved;
}

export function __testing__resetHostPool(): void {
  signed = null;
  failoverInflight = null;
  for (const key of [PIN_KEY, LIST_KEY, MOVED_KEY]) write(key, null);
}
